import { pendingScanRepository } from '@/db/repositories/pendingScanRepository';
import { cacheRepository } from '@/db/repositories/cacheRepository';
import type { LeadRating } from '@/shared/types/domain';

export class MissingExhibitorContextError extends Error {
  constructor() {
    super(
      'No cached exhibitor context is available. This can only happen if you have ' +
        'never logged in on this device while online -- please connect to the internet once to continue.'
    );
  }
}

interface CaptureScanInput {
  scannedValue: string;
  userId: string;
  notes?: string | null;
  rating?: LeadRating | null;
}

export const scanCaptureService = {
  /**
   * Writes a captured scan directly to IndexedDB and returns immediately --
   * this function never makes a network call and never awaits one. That is
   * the entire point of it: per the offline-first requirement, a scan must
   * be durably saved and the UI must be free to show success and return to
   * scanning before any server round-trip could possibly complete, let
   * alone succeed.
   */
  async captureScan(input: CaptureScanInput): Promise<string> {
    const context = await cacheRepository.getExhibitorContext();

    if (!context) {
      // This should be unreachable in normal operation -- AuthProvider
      // caches the exhibitor context immediately on every successful login,
      // which is required before any protected route (including the
      // scanner) is reachable at all. Guarded explicitly anyway rather than
      // risking an upload with a blank eventId/exhibitorId.
      throw new MissingExhibitorContextError();
    }

    const localId = crypto.randomUUID();

    await pendingScanRepository.add({
      localId,
      eventId: context.eventId,
      exhibitorId: context.exhibitorId,
      userId: input.userId,
      scannedValue: input.scannedValue,
      scannedAtUtc: new Date().toISOString(),
      notes: input.notes ?? null,
      rating: input.rating ?? null
    });

    return localId;
  }
};
